import type { PanelMapping } from "@skyware-group/iris-api-model";

import { BaseClient } from "../../clients/base/BaseClient";
import type { Paged, PaginationParams } from "../_shared/pagination.types";
import { PANEL_MAPPINGS_ENDPOINTS } from "./panel-mappings.consts";
import type { PanelMappingCreateRequest, PanelMappingUpdateRequest } from "./panel-mappings.types";

/**
 * Panel Mappings API Client
 * Manages LIS mappings for examination panels.
 */
export class PanelMappingsClient extends BaseClient {
  /**
   * Fetch a page of mappings for a panel.
   * @param instrumentId - Instrument ID.
   * @param panelId - Panel ID.
   * @param params - Optional pagination parameters.
   * @param signal - Optional abort signal.
   */
  async fetchPanelMappings(
    instrumentId: number,
    panelId: number,
    params?: PaginationParams,
    signal?: AbortSignal | null,
  ): Promise<Paged<PanelMapping>> {
    return this.get<Paged<PanelMapping>>(PANEL_MAPPINGS_ENDPOINTS.PANEL_MAPPINGS(instrumentId, panelId), {
      params: params as Record<string, unknown>,
      signal,
    });
  }

  /**
   * Create a new panel mapping.
   * @param instrumentId - Instrument ID.
   * @param panelId - Panel ID.
   * @param data - Mapping create request.
   * @param signal - Optional abort signal.
   */
  async createPanelMapping(
    instrumentId: number,
    panelId: number,
    data: PanelMappingCreateRequest,
    signal?: AbortSignal | null,
  ): Promise<PanelMapping> {
    return this.post<PanelMapping>(PANEL_MAPPINGS_ENDPOINTS.CREATE_PANEL_MAPPING(instrumentId, panelId), {
      body: data,
      signal,
    });
  }

  /**
   * Update an existing panel mapping by its natural key.
   * @param instrumentId - Instrument ID.
   * @param panelId - Panel ID.
   * @param mappingKey - Mapping natural key.
   * @param data - Mapping update request.
   * @param signal - Optional abort signal.
   */
  async updatePanelMapping(
    instrumentId: number,
    panelId: number,
    mappingKey: string,
    data: PanelMappingUpdateRequest,
    signal?: AbortSignal | null,
  ): Promise<PanelMapping> {
    return this.put<PanelMapping>(PANEL_MAPPINGS_ENDPOINTS.PANEL_MAPPING_BY_KEY(instrumentId, panelId, mappingKey), {
      body: data,
      signal,
    });
  }

  /**
   * Delete a panel mapping by its natural key.
   * @param instrumentId - Instrument ID.
   * @param panelId - Panel ID.
   * @param mappingKey - Mapping natural key.
   * @param signal - Optional abort signal.
   */
  async deletePanelMapping(
    instrumentId: number,
    panelId: number,
    mappingKey: string,
    signal?: AbortSignal | null,
  ): Promise<void> {
    return this.delete<never>(PANEL_MAPPINGS_ENDPOINTS.PANEL_MAPPING_BY_KEY(instrumentId, panelId, mappingKey), {
      signal,
    });
  }
}
